import { Component } from '@atomify/hooks';
import { BPDInput } from '@source/components/atoms';
import { requestAddress } from '@utilities/api-calls';
import { isNetherlands } from '@utilities/is-netherlands';

interface PostcodeCheckerHookInterface {
    postcodeHook: string;
    houseNumberHook: string;
    houseNumberAdditionHook: string;
    streetNameHook: string;
    placeNameHook: string;
}

interface PostcodeCheckerParams {
    element: Component;
    hooks: PostcodeCheckerHookInterface;
    onAddressUpdated?: Function;
}

export const usePostcodeChecker = ({ element, hooks, onAddressUpdated }: PostcodeCheckerParams) => {
    if (!isNetherlands()) return;

    let postcodeBpdInput: BPDInput | null;
    let houseNumberBpdInput: BPDInput | null = null;
    let houseNumberAdditionBpdInput: BPDInput | null = null;
    let streetNameBpdInput: BPDInput | null = null;
    let placeNameBpdInput: BPDInput | null = null;

    let streetNameInput: HTMLInputElement | null = null;
    let placeNameInput: HTMLInputElement | null = null;
    let isEnabled = false;
    let addressUpdated = false;

    const queryBPDInput = (hook: string) => element.querySelector<BPDInput>(hook);

    const queryHTMLInput = (input: BPDInput | null) =>
        input?.querySelector<HTMLInputElement>('input') || null;

    const initialize = () => {
        postcodeBpdInput = queryBPDInput(hooks.postcodeHook);
        houseNumberBpdInput = queryBPDInput(hooks.houseNumberHook);
        houseNumberAdditionBpdInput = queryBPDInput(hooks.houseNumberAdditionHook);
        streetNameBpdInput = queryBPDInput(hooks.streetNameHook);
        placeNameBpdInput = queryBPDInput(hooks.placeNameHook);

        streetNameInput = queryHTMLInput(streetNameBpdInput);
        placeNameInput = queryHTMLInput(placeNameBpdInput);

        enable();
    };

    const enable = () => {
        if (isEnabled) return;

        postcodeBpdInput?.addEventListener('change', updateAddress);
        houseNumberBpdInput?.addEventListener('change', updateAddress);
        houseNumberAdditionBpdInput?.addEventListener('change', updateAddress);

        streetNameInput?.setAttribute('disabled', 'disabled');
        placeNameInput?.setAttribute('disabled', 'disabled');

        revalidateInputs();
        isEnabled = true;
    };

    const disable = () => {
        if (!isEnabled) return;

        postcodeBpdInput?.removeEventListener('change', updateAddress);
        houseNumberBpdInput?.removeEventListener('change', updateAddress);
        houseNumberAdditionBpdInput?.removeEventListener('change', updateAddress);

        streetNameInput?.removeAttribute('disabled');
        placeNameInput?.removeAttribute('disabled');

        revalidateInputs();
        isEnabled = false;
    };

    function revalidateInputs() {
        if (postcodeBpdInput?.bindValue) postcodeBpdInput?.validate();
        if (houseNumberBpdInput?.bindValue) houseNumberBpdInput?.validate();
        if (houseNumberAdditionBpdInput?.bindValue) houseNumberAdditionBpdInput?.validate();
        if (streetNameBpdInput?.bindValue) streetNameBpdInput.validate();
        if (placeNameBpdInput?.bindValue) placeNameBpdInput.validate();
    }

    const isValidPostcode = () =>
        postcodeBpdInput?.valid &&
        houseNumberBpdInput?.valid &&
        (houseNumberAdditionBpdInput?.valid || !houseNumberAdditionBpdInput?.bindValue);

    const isValidAddress = () => streetNameBpdInput?.valid && placeNameBpdInput?.valid;

    const updateAddress = async () => {
        if (isValidPostcode()) {
            try {
                const address = await requestAddress({
                    postcode: postcodeBpdInput!.bindValue,
                    houseNumber: houseNumberBpdInput!.bindValue,
                    houseNumberAddition: houseNumberAdditionBpdInput?.bindValue,
                });

                if (!address) return;

                const { Street, City } = address;

                streetNameBpdInput?.rebindValue(Street);
                placeNameBpdInput?.rebindValue(City);
            } finally {
                streetNameInput?.removeAttribute('disabled');
                placeNameInput?.removeAttribute('disabled');

                addressUpdated = checkValidity();
                if (addressUpdated) onAddressUpdated && onAddressUpdated();
            }
        }
    };

    function checkValidity() {
        revalidateInputs();
        return (isValidPostcode() && isValidAddress()) || false;
    }

    return { initialize, updateAddress, enable, disable, checkValidity, isEnabled };
};
